// 組織樹、檢視、群組與委派的 app-api（SPEC #org-tree-api、#org-tree-views、#org-tree-delegation）。
// wire 名稱照 SPEC arch-2-6：節點 id 一律 node_id / parent_node_id（DB 仍是 site_id）；/sites 已改名 /nodes、不留相容層，跟 platform M3a 成對部署。
// 回應形狀 SPEC 沒逐欄寫死的（節點清單、存取、預覽的列）照 SPEC 欄位名暫定，M3a 開 MR 後對齊（回報 reports/ot-vms.md「對不上的地方」）。
import { kinioFetch, planeBase } from '@kinio/web-sdk';
import type { OrgRef, Plane } from './planeApi';

function orgUrl(o: OrgRef, path: string): string {
  return `${planeBase(o.api)}/v1/orgs/${encodeURIComponent(o.org_id)}${path}`;
}
function qs(q: Record<string, string | number | boolean | undefined | null>): string {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== null && v !== '' && v !== false) s.set(k, String(v));
  return s.size ? '?' + s : '';
}
const enc = encodeURIComponent;

// ---- 層級名稱（#org-tree-model） ----

export interface TreeSettings {
  /** null = 內建清單（@site / @building / @floor / @zone） */
  levels: string[] | null;
  /** 家戶的顯示名稱；null = 「家戶」（個人組織「家」） */
  leaf_label: string | null;
  version?: number;
}

export const BUILTIN_LEVELS = ['@zone', '@site', '@building', '@floor'] as const;

// ---- 節點與家戶（結構，設定頁用；數字 = 結構總數） ----

/** GET /v1/homes/{id} 的 export_archives：retention = archive、status = done 的包數與最晚到期（沒有 = {count: 0, latest_expire_at: null}）。 */
/** 家戶成員（GET /v1/homes/{id}/members）：role_source = default（跟著組織角色）/ override / explicit。 */
export interface HomeMember {
  user_id: string;
  role: string;
  role_source?: 'default' | 'override' | 'explicit' | string;
  email?: string;
  display_name?: string;
  is_me?: boolean;
}
export interface HomeMembers {
  home_id: string;
  /** 我在這一戶的角色 */
  role: string;
  members: HomeMember[];
}

export interface ExportArchives {
  count: number;
  latest_expire_at: string | null;
}

export interface Subtree {
  nodes: number;
  homes: number;
  /** 設備數；後端回了 channels 時不含通道，沒回 channels 的舊後端含通道（SPEC #org-tree-ui 設備數一律不含通道） */
  devices: number;
  /** NVR / Kinio Station 的通道數（後端分開回之後才有） */
  channels?: number;
}

/** GET /v1/orgs/{org}/nodes 的一項（節點或家戶）。 */
export interface OrgTreeItem {
  kind: 'node' | 'home';
  node_id?: string;
  home_id?: string;
  /** 節點：上層節點；家戶：掛在哪個節點 */
  parent_node_id?: string | null;
  name: string;
  /** 節點的層級名稱（原值，內建鍵 @floor…，畫面依語系翻）；根節點 null */
  level_label?: string | null;
  has_children?: boolean;
  subtree?: Subtree;
  /** 🔒 中斷繼承：只回給 owner / org_admin */
  block_inheritance?: boolean;
  /** 家戶：assigned / unassigned（owner / org_admin / org.sites 才有） */
  management_status?: string;
  /** 家戶的管理人（交接 / 接管用） */
  owner_user_id?: string;
  /** 搜尋結果：祖先路徑 */
  path?: { node_id: string; name: string }[];
  /** 家戶的設備數（結構總數） */
  devices?: number;
  status?: 'active' | 'archived';
  version: number;
}

export interface OrgNode {
  node_id: string;
  parent_node_id: string | null;
  name: string;
  level_label: string | null;
  block_inheritance?: boolean;
  status?: 'active' | 'archived';
  version: number;
  subtree?: Subtree;
  /** 從根（或 node_admin 的可見起點）到自己的祖先（不含自己） */
  path?: { node_id: string; name: string }[];
  timezone?: string;
  geo?: { lat?: number; lng?: number; address?: string; country?: string };
  /** 斷網可用（SPEC #vms-web-offline「誰能用」）：cloud_required | offline_allowed */
  media_mode?: string;
  /** 斷網可用的時數上限（team 組織最多 72） */
  offline_max_hours?: number;
}

export interface NodeListPage {
  items: OrgTreeItem[];
  /** 平台若回根節點（parent 不帶時） */
  root?: OrgNode;
  next_cursor?: string;
}

// ---- 指派（#org-tree-inheritance） ----

export type NodeRole = 'node_admin' | 'operator' | 'viewer' | 'installer' | 'maintainer' | 'custom';
export const NODE_ROLES: NodeRole[] = ['node_admin', 'operator', 'viewer', 'installer', 'maintainer', 'custom'];

/** 角色動作（SPEC #org-tree-inheritance 表）。自訂範本看範本的動作。 */
export const ROLE_ACTIONS: Record<Exclude<NodeRole, 'custom'>, string[]> = {
  node_admin: ['device.read', 'media.live', 'media.talk', 'event.read', 'recording.read', 'device.control', 'alarm.receive', 'security.arm', 'security.disarm', 'device.maintain'],
  operator: ['device.read', 'media.live', 'media.talk', 'event.read', 'recording.read', 'device.control', 'alarm.receive'],
  viewer: ['device.read', 'media.live', 'event.read'],
  installer: ['device.read', 'media.live', 'device.control'],
  // 維運員：看設備 + 維運（OTA、重開機、改名）；KMS 只推導 kmgr.read。給同事維運 Kinio Node / Wi-Fi 基地台（Eddy 2026-10-16 選單 A）
  maintainer: ['device.read', 'device.maintain'],
};
/** 單台（site 授權）給維運員的動作組合：帶 device.maintain 的 site 授權只能是這一組（SPEC #org-tree-inheritance）。 */
export const MAINTAINER_GRANT_ACTIONS = ['device.read', 'device.maintain', 'kmgr.read'];
/** 單台（site 授權，POST /nodes/{node}/grants）收的動作：平台 authz.AllActions 扣掉還沒開放的 case.*（vms#32）。
 *  收警報 / 布撤防只經組織分享或節點指派，放進 grant 會 400 BAD_ACTION。 */
export const GRANT_ACTIONS = ['device.read', 'device.control', 'media.live', 'media.talk', 'recording.read', 'recording.export', 'event.read', 'energy.read'];
/** extra_actions 可勾的（上限照「誰能指派」）。 */
export const EXTRA_ACTIONS = ['recording.export', 'alarm.receive', 'security.arm', 'security.disarm', 'device.maintain'] as const;
/** P6（受託方）指派一律不從角色帶入的動作（Eddy 2026-10-08 選單 19）。 */
export const P6_EXPLICIT = ['security.arm', 'security.disarm', 'device.maintain', 'recording.export'];

export interface Schedule {
  tz?: string;
  windows: { days?: number[]; from: string; to: string }[];
}

export interface Assignment {
  assignment_id: string;
  node_id: string;
  node_path?: string[];
  delegation_id?: string | null;
  principal_kind: 'user' | 'group';
  principal_id: string;
  display_name: string;
  /** 成員群組展開的人數 */
  member_count?: number;
  role: NodeRole;
  role_template_ref?: string | null;
  role_name?: string;
  extra_actions?: string[];
  actions: string[];
  /** 本機錄影（SD / 硬碟）回放視同全部歷史（SPEC #device-permissions-local-playback）；只有 owner / org_admin 能設 */
  local_playback_full?: boolean;
  effective_actions?: string[];
  ignored_actions?: string[];
  out_of_scope?: string[];
  applicable?: string[];
  expires_at?: string | null;
  schedule?: Schedule | null;
  granted_by?: string;
  granted_by_name?: string;
  is_self?: boolean;
  active_streams?: number;
  version: number;
}

export interface NodeAccess {
  node_id: string;
  block_inheritance?: boolean;
  /** 本節點的指派 */
  assignments: Assignment[];
  /** 從上層繼承來的（唯讀） */
  inherited: (Assignment & { from_node_id: string; from_node_name: string })[];
  /** 委派出去的（本節點或上層的委派涵蓋這裡；唯讀） */
  delegations: {
    delegation_id: string;
    to_org_name: string;
    to_org_verified_kinds?: string[];
    scopes: string[];
    from_node_id: string;
    from_node_name: string;
    expires_at?: string | null;
    review_due?: boolean;
  }[];
}

export interface AssignmentIn {
  principal_kind: 'user' | 'group';
  principal_id: string;
  role: NodeRole;
  role_template_ref?: string;
  extra_actions?: string[];
  expires_at?: string | null;
  schedule?: Schedule | null;
  reason?: string;
  local_playback_full?: boolean;
}

// ---- 影響預覽（#org-tree-preview-fields） ----

export interface PreviewPerson {
  principal_kind: 'user' | 'group' | 'org';
  principal_id?: string;
  display_name: string;
  /** 成員群組展開的人數；受託組織 = 被指派者人數 */
  member_count?: number;
  role?: string;
  /** 指派所在（來源）節點 */
  from_node_name?: string;
  /** 他在受影響家戶還有 P1–P4 */
  other_paths?: boolean;
  /** gaining：從這個時刻起算（涵蓋起點） */
  history_visible_from?: string;
  is_self?: boolean;
  grantee_ref?: string;
  lost_actions?: string[];
}

export interface PreviewDelegation {
  delegation_id: string;
  to_org_name: string;
  change: 'gained' | 'lost';
  /** 涵蓋變大的是高風險委派 → 送出要帶 reason（platform#90） */
  reason_required?: boolean;
  grantee_count?: number;
  homes?: number;
  scopes?: string[];
  from_node_name?: string;
}

export interface RemainingAccess {
  /** also_revoke 用的 id（assignment_id / grant_id / share_id / home member ref） */
  ref_id: string;
  kind: 'home_role' | 'site_grant' | 'share_user' | 'share_org' | 'assignment' | 'delegation';
  display_name: string;
  /** 家戶角色（owner / admin…）、分享範圍（看…）、角色 */
  detail?: string;
  /** 範圍：「501 全部」「501 / 走廊」 */
  scope_label?: string;
  granted_by_name?: string;
  /** 授予者這次寫入後在受影響家戶已沒有任何路徑 → 預設勾〔一併撤銷〕（選單 20） */
  granted_by_losing: boolean;
  /** 授予者還有哪條路徑（「家戶角色」…）——不預設勾、寫「授予者仍經 … 看得到」 */
  granted_by_other_path?: string;
  /** 對象本人在 losing 裡（他靠這條仍看得到）——不預設勾、醒目 */
  target_is_losing?: boolean;
  /** 能不能撤（操作者有權撤的才可勾） */
  revocable?: boolean;
  /** 擁有者這種保留列 */
  keep?: boolean;
}

export interface Preview {
  preview_token: string;
  gaining: PreviewPerson[];
  losing: PreviewPerson[];
  delegations: PreviewDelegation[];
  homes_affected?: number;
  devices_affected?: number;
  active_streams?: number;
  site_grants_revoked?: number;
  total_gaining?: number;
  total_losing?: number;
  self_gain?: boolean;
  remaining_access?: RemainingAccess[];
  granted_by_target?: Assignment[];
  /** 擴大到高風險委派（含移動 / 換掛讓高風險委派涵蓋變大）→ 送出要帶 reason，立即生效（platform#90） */
  reason_required?: boolean;
}

export interface MoveResult {
  revoked?: number;
}

// ---- 成員群組 ----

export interface MemberGroup {
  group_id: string;
  name: string;
  version: number;
  member_count: number;
  members?: { user_id: string; display_name?: string; email?: string }[];
  /** 被多少筆指派使用（加自己進有指派的群組 = 自我授權） */
  assignment_count?: number;
}

// ---- 委派（#org-tree-delegation） ----

export const DELEGATION_SCOPES = [
  'device.read',
  'media.live',
  'event.read',
  'recording.read',
  'alarm.receive',
  'media.talk',
  'device.control',
  'security.arm',
  'security.disarm',
  'device.maintain',
  'home.profile.read',
  'recording.export',
] as const;
/** 高風險委派的範圍（SPEC #org-tree-delegation；另含根節點）：建立或擴大要填理由、立即生效、通知雙方管理員（platform#90） */
export const HIGH_RISK_SCOPES = ['recording.export', 'security.disarm'];

export interface DelegationNode {
  node_id: string;
  name: string;
  level_label?: string | null;
  /** 根節點 = 整個組織 */
  is_root?: boolean;
  subtree?: Subtree;
}

export interface Delegation {
  delegation_id: string;
  from_org_id: string;
  from_org_name: string;
  to_org_id: string;
  to_org_name: string;
  /** 受託組織的組織代號（雙方口頭核對） */
  to_org_code?: string;
  to_org_verified_kinds?: string[];
  granted_by_name?: string;
  nodes: DelegationNode[];
  scopes: string[];
  status: 'active' | 'ended' | 'suspended';
  valid_from?: string;
  history_from?: string | null;
  /** 委派方允許受託方的指派回放本機錄影全部歷史（D 的指派還要自己打開） */
  local_playback_full?: boolean;
  expires_at?: string | null;
  reviewed_at?: string;
  review_due?: boolean;
  expiring?: boolean;
  /** 受託方：A 擴大後可〔套用〕的指派筆數 */
  expandable_count?: number;
  grantee_count?: number;
  /** 委派方要求兩步驟驗證 */
  from_mfa_required?: boolean;
  created_at?: string;
  expanded_at?: string | null;
  ended_at?: string | null;
  end_reason?: string | null;
  version: number;
  /** 委派方詳情：D 的被指派人（唯讀，W-02） */
  grantee_assignments?: {
    grantee_ref: string;
    display_name: string;
    principal_kind: 'user' | 'group';
    via_group_ref?: string;
    role: string;
    effective_actions: string[];
    node_id: string;
    node_name?: string;
    expires_at?: string | null;
  }[];
  /** 最近 30 天經這筆委派的操作數 */
  recent_ops?: number;
}

export interface DelegationRequest {
  request_id: string;
  code: string;
  link?: string;
  expires_at: string;
  status: 'pending' | 'accepted' | 'expired' | 'canceled';
  /** 本組織代號 */
  org_code?: string;
  note?: string;
}

export interface DelegationCodePreview {
  to_org_id: string;
  to_org_name: string;
  to_org_code?: string;
  verified_kinds?: string[];
  requested_by_name?: string;
  requested_by_email_domain?: string;
  /** 操作者自己也是受託組織的 active 成員 → reason 必填 */
  self_related?: boolean;
}

export interface DelegationIn {
  code: string;
  node_ids: string[];
  scopes: string[];
  expires_at?: string | null;
  history_from?: string | null;
  reason?: string;
  local_playback_full?: boolean;
}

/** GET /v1/orgs/{D}/delegations/{d}/nodes 的一項（只有名稱與結構數）。 */
export interface DelegatedNodeItem {
  kind: 'node' | 'home';
  node_id?: string;
  home_id?: string;
  name: string;
  level_label?: string | null;
  has_children?: boolean;
  subtree?: Subtree;
  devices?: { device_ref: string; display_name: string }[];
  path?: string[];
}

// ---- /v1/me/tree（即時頁的樹；數字 = 我看得到的） ----

export interface MeTreeItem {
  key: string;
  kind: 'org' | 'delegation' | 'node' | 'home' | 'device' | 'sharer';
  parent_key?: string | null;
  id: string;
  name: string;
  level_label?: string | null;
  has_children?: boolean;
  device_count?: number;
  /** 只回給能管理該節點的人 */
  blocked?: boolean;
  delegation?: {
    delegation_id: string;
    from_org_id: string;
    from_org_name: string;
    expires_at?: string | null;
    notice?: 'delegator_mfa_required' | null;
    /** 委派根節點（整個組織） */
    whole_org?: boolean;
  } | null;
  /** kind = delegation：active / ended（結束後 24 小時內保留） */
  status?: 'active' | 'ended';
  ended_at?: string;
  end_reason?: string;
  /** kind = org：我是不是 owner（歸「我自己的」） */
  source?: 'own' | 'org' | 'shared';
  org_kind?: 'personal' | 'team';
  path?: { key: string; name: string }[];
  /** kind = home：所在 home_id；device：home_id + device_ref */
  home_id?: string;
  device_ref?: string;
}

export interface MeTreePage {
  items: MeTreeItem[];
  more?: boolean;
  access_revision?: string;
  next_cursor?: string;
}

// ---- 檢視與群組（#org-tree-views） ----

export type ViewItemKind = 'folder' | 'node' | 'home' | 'device' | 'group';

export interface ViewItem {
  item_id?: string;
  parent_item_id?: string | null;
  kind: ViewItemKind;
  /** node_id / home_id / home_id/device_ref / group_id */
  ref?: string;
  name?: string;
  /** 委派來的參照：委派方名稱（⤳ 小標） */
  from_org_name?: string;
  delegation_id?: string;
  /** 編輯者已不能參照：gone = 其他原因；delegation_ended = 委派結束（不回原名稱） */
  unavailable?: 'gone' | 'delegation_ended';
  sort_key?: number;
}

export interface View {
  view_id: string;
  owner_kind: 'user' | 'org';
  owner_id: string;
  owner_name?: string;
  name: string;
  version: number;
  items: ViewItem[];
  can_edit?: boolean;
}

export interface DeviceGroup {
  group_id: string;
  owner_kind: 'user' | 'org';
  owner_id: string;
  owner_name?: string;
  name: string;
  version: number;
  items: ViewItem[];
  can_edit?: boolean;
}

export interface ResolvedDevice {
  home_id: string;
  device_ref: string;
  display_name: string;
  /** 「<委派方> / <節點> / <戶>」 */
  location?: string;
  actions?: { action: string }[];
}

export interface Resolved {
  folders: { item_id: string; parent_item_id: string | null; name: string; device_count: number }[];
  items: { item_id: string; parent_item_id: string | null; kind: ViewItemKind; ref: string; name: string; device_count: number; from_org_name?: string }[];
  devices: ResolvedDevice[];
  device_total: number;
  unavailable_count?: number;
  access_revision?: string;
  next_cursor?: string;
}

// ---- 通知訂閱 ----

export interface ScopeSubscription {
  node_id?: string;
  group_id?: string;
  delegation_id?: string;
  /** 顯示用的路徑（委派的從「委派：<委派方>」起） */
  path?: string[];
  name?: string;
  categories: Record<string, { push: boolean; email: boolean; inbox: boolean }>;
  active: boolean;
}

export interface ImplicitNotice {
  kind: 'alarm' | 'device_status';
  /** 「委派：安和物產 › 北區」 */
  where: string;
  role: string;
  /** 警報：能處理（照推）/ 觀看者（不推）；設備異常：收 */
  push: boolean;
}

// ---- API ----

/** 一頁一頁翻完（上限 pages 頁，免得失控）。 */
async function allPages<T>(fetchPage: (cursor: string) => Promise<{ items: T[]; next_cursor?: string } | null | undefined>, pages = 20): Promise<T[]> {
  const out: T[] = [];
  let cursor = '';
  for (let i = 0; i < pages; i++) {
    const r = await fetchPage(cursor);
    out.push(...(r?.items ?? []));
    cursor = r?.next_cursor ?? '';
    if (!cursor) break;
  }
  return out;
}

export const treeApi = {
  settings: (o: OrgRef) => kinioFetch<TreeSettings>(orgUrl(o, '/tree-settings')),
  putSettings: (o: OrgRef, body: { levels: string[] | null; leaf_label: string | null; from_builtin?: Record<string, string>; expected_version?: number }) =>
    kinioFetch<TreeSettings>(orgUrl(o, '/tree-settings'), { method: 'PUT', body }),

  /** 子節點與子家戶（延遲載入）；parent 不帶 = 根節點底下。 */
  nodes: (o: OrgRef, q: { parent?: string; q?: string; level?: string; cursor?: string; limit?: number } = {}) =>
    kinioFetch<NodeListPage>(orgUrl(o, '/nodes' + qs(q))),
  /** 一層全部（翻頁）。 */
  children: async (o: OrgRef, parent?: string) => {
    let root: OrgNode | undefined;
    const items = await allPages(async (cursor) => {
      const r = await treeApi.nodes(o, { parent, cursor, limit: 1000 });
      root = root ?? r?.root;
      return r;
    });
    return { items, root };
  },
  node: async (o: OrgRef, nodeId: string): Promise<OrgNode> => {
    const r = await kinioFetch<OrgNode | { node: OrgNode }>(orgUrl(o, '/nodes/' + enc(nodeId)));
    return ('node' in (r as object) ? (r as { node: OrgNode }).node : (r as OrgNode)) as OrgNode;
  },
  createNode: (o: OrgRef, body: { parent_node_id: string; name: string; level_label: string }) => kinioFetch<OrgNode>(orgUrl(o, '/nodes'), { method: 'POST', body }),
  updateNode: (
    o: OrgRef,
    nodeId: string,
    body: {
      name?: string;
      level_label?: string;
      parent_node_id?: string;
      expected_version: number;
      preview_token?: string;
      also_revoke?: string[];
      reason?: string;
      timezone?: string;
      geo?: OrgNode['geo'];
      media_mode?: 'cloud_required' | 'offline_allowed';
      offline_max_hours?: number;
    },
  ) => kinioFetch<MoveResult & { node?: OrgNode }>(orgUrl(o, '/nodes/' + enc(nodeId)), { method: 'PATCH', body }),
  previewNode: (o: OrgRef, nodeId: string, body: { parent_node_id?: string; block_inheritance?: boolean }) =>
    kinioFetch<Preview>(orgUrl(o, '/nodes/' + enc(nodeId) + '/preview'), { method: 'POST', body }),
  blockInheritance: (o: OrgRef, nodeId: string, body: { enabled: boolean; expected_version: number; preview_token: string; also_revoke?: string[]; reason?: string }) =>
    kinioFetch<OrgNode>(orgUrl(o, '/nodes/' + enc(nodeId) + '/block-inheritance'), { method: 'PUT', body }),
  archiveNode: (o: OrgRef, nodeId: string, expectedVersion: number) =>
    kinioFetch<OrgNode>(orgUrl(o, '/nodes/' + enc(nodeId) + '/archive'), { method: 'POST', body: { expected_version: expectedVersion } }),
  access: (o: OrgRef, nodeId: string) => kinioFetch<NodeAccess>(orgUrl(o, '/nodes/' + enc(nodeId) + '/access')),

  createHome: (o: OrgRef, body: { display_name: string; node_id?: string; manager_user_id?: string; reason?: string; timezone?: string }) =>
    kinioFetch<{ home_id: string; management_status: string; version: number }>(orgUrl(o, '/homes'), { method: 'POST', body }),
  previewHome: (o: OrgRef, homeId: string, nodeId: string) => kinioFetch<Preview>(orgUrl(o, '/homes/' + enc(homeId) + '/preview'), { method: 'POST', body: { node_id: nodeId } }),
  moveHome: (o: OrgRef, homeId: string, body: { node_id: string; expected_version: number; preview_token: string; also_revoke?: string[]; reason?: string }) =>
    kinioFetch<MoveResult>(orgUrl(o, '/homes/' + enc(homeId)), { method: 'PATCH', body }),
  previewHomes: (o: OrgRef, homeIds: string[], nodeId: string) => kinioFetch<Preview>(orgUrl(o, '/homes/move/preview'), { method: 'POST', body: { home_ids: homeIds, node_id: nodeId } }),
  moveHomes: (
    o: OrgRef,
    body: { home_ids: string[]; node_id: string; expected_versions: Record<string, number>; preview_token: string; also_revoke?: string[]; reason?: string },
  ) => kinioFetch<MoveResult & { batch_id?: string }>(orgUrl(o, '/homes/move'), { method: 'POST', body }),
  /** 刪組織底下的家戶（SPEC #org-tree-api、#org-tree-ui-home-delete；5 分鐘內重新驗證，REAUTH_REQUIRED 由 SDK 走 StepUpHost 後重送） */
  deleteHome: (o: OrgRef, homeId: string) => kinioFetch<{ purged?: boolean }>(orgUrl(o, '/homes/' + enc(homeId)), { method: 'DELETE' }),
  /** 家戶詳細的長期保管匯出包摘要（SPEC #vms-web-export-archive「刪家戶」）：能管理的人才有，其他人 / 舊後端是 null */
  homeArchives: (o: OrgRef, homeId: string) =>
    kinioFetch<{ export_archives?: ExportArchives | null }>(`${planeBase(o.api)}/v1/homes/${enc(homeId)}`).then((r) => r.export_archives ?? null),
  /** 這一戶的成員與角色（GET /v1/homes/{id}/members；任何成員可看，role = 我在這一戶的角色；vms#33） */
  homeMembers: (o: OrgRef, homeId: string) => kinioFetch<HomeMembers>(`${planeBase(o.api)}/v1/homes/${enc(homeId)}/members`),
  /** 移出這一戶（只能拿掉直接指定 / 覆寫的角色；用組織角色預設的人回 404 USER_NOT_FOUND；vms#33） */
  removeHomeMember: (o: OrgRef, homeId: string, userId: string) =>
    kinioFetch<{ removed: boolean }>(`${planeBase(o.api)}/v1/homes/${enc(homeId)}/members/${enc(userId)}`, { method: 'DELETE' }),
  /** 批次指定管理人（只收 unassigned；step-up、Idempotency-Key） */
  assignManagers: (o: OrgRef, body: { home_ids: string[]; target_user_id: string; expected_versions: Record<string, number>; reason: string }, idempotencyKey: string) =>
    kinioFetch<{ batch_id: string; homes: number }>(orgUrl(o, '/homes/owner-transfers'), { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey } }),

  createAssignment: (o: OrgRef, nodeId: string, body: AssignmentIn) =>
    kinioFetch<Assignment>(orgUrl(o, '/nodes/' + enc(nodeId) + '/assignments'), { method: 'POST', body }),
  updateAssignment: (o: OrgRef, nodeId: string, a: Pick<Assignment, 'assignment_id' | 'version'>, body: Partial<AssignmentIn> & { preview_token?: string; also_revoke?: string[] }) =>
    kinioFetch<Assignment>(orgUrl(o, `/nodes/${enc(nodeId)}/assignments/${enc(a.assignment_id)}`), { method: 'PATCH', body: { expected_version: a.version, ...body } }),
  previewRevoke: (o: OrgRef, nodeId: string, assignmentId: string) =>
    kinioFetch<Preview>(orgUrl(o, `/nodes/${enc(nodeId)}/assignments/${enc(assignmentId)}/preview`), { method: 'POST', body: { revoke: true } }),
  /** 平台 nodeAssignRevoke 從 body 讀 expected_version（沒帶 = 0 → 409 CONFLICT「資料已被別人更新」；kinio/app/vms#32） */
  revokeAssignment: (o: OrgRef, nodeId: string, a: Pick<Assignment, 'assignment_id' | 'version'>, body: { preview_token?: string; also_revoke?: string[] } = {}) =>
    kinioFetch(orgUrl(o, `/nodes/${enc(nodeId)}/assignments/${enc(a.assignment_id)}`), { method: 'DELETE', body: { expected_version: a.version, ...body } }),

  memberGroups: (o: OrgRef) => kinioFetch<{ groups: MemberGroup[] }>(orgUrl(o, '/member-groups')).then((r) => r?.groups ?? []),
  memberGroup: (o: OrgRef, id: string) => kinioFetch<MemberGroup>(orgUrl(o, '/member-groups/' + enc(id))),
  createMemberGroup: (o: OrgRef, body: { name: string; member_ids: string[]; reason?: string }) => kinioFetch<MemberGroup>(orgUrl(o, '/member-groups'), { method: 'POST', body }),
  updateMemberGroup: (o: OrgRef, g: Pick<MemberGroup, 'group_id' | 'version'>, body: { name?: string; member_ids?: string[]; reason?: string }) =>
    kinioFetch<MemberGroup>(orgUrl(o, '/member-groups/' + enc(g.group_id)), { method: 'PATCH', body: { expected_version: g.version, ...body } }),
  /** 平台 memberGroupDelete 從 JSON body 讀 expected_version（不是 query；空 body = 400 BAD_JSON，kinio/app/vms#23） */
  deleteMemberGroup: (o: OrgRef, g: Pick<MemberGroup, 'group_id' | 'version'>) =>
    kinioFetch(orgUrl(o, '/member-groups/' + enc(g.group_id)), { method: 'DELETE', body: { expected_version: g.version } }),

  // 委派
  createRequest: (o: OrgRef, note?: string) => kinioFetch<DelegationRequest>(orgUrl(o, '/delegation-requests'), { method: 'POST', body: note ? { note } : {} }),
  requests: (o: OrgRef) => kinioFetch<{ requests: DelegationRequest[]; org_code?: string }>(orgUrl(o, '/delegation-requests')),
  cancelRequest: (o: OrgRef, id: string) => kinioFetch(orgUrl(o, '/delegation-requests/' + enc(id)), { method: 'DELETE' }),
  previewCode: (o: OrgRef, code: string) => kinioFetch<DelegationCodePreview>(orgUrl(o, '/delegations/preview'), { method: 'POST', body: { code } }),
  createDelegation: (o: OrgRef, body: DelegationIn) =>
    kinioFetch<{ delegation: Delegation }>(orgUrl(o, '/delegations'), { method: 'POST', body }),
  delegations: (o: OrgRef, direction: 'out' | 'in') => kinioFetch<{ delegations: Delegation[] }>(orgUrl(o, '/delegations' + qs({ direction }))).then((r) => r?.delegations ?? []),
  delegation: (o: OrgRef, id: string) => kinioFetch<Delegation>(orgUrl(o, '/delegations/' + enc(id))),
  previewDelegation: (o: OrgRef, id: string, body: { scopes?: string[]; node_ids?: string[]; expires_at?: string | null; end?: boolean }) =>
    kinioFetch<Preview>(orgUrl(o, '/delegations/' + enc(id) + '/preview'), { method: 'POST', body }),
  updateDelegation: (o: OrgRef, d: Pick<Delegation, 'delegation_id' | 'version'>, body: { scopes?: string[]; node_ids?: string[]; expires_at?: string | null; preview_token: string; reason?: string }) =>
    kinioFetch<{ delegation: Delegation }>(orgUrl(o, '/delegations/' + enc(d.delegation_id)), {
      method: 'PATCH',
      body: { expected_version: d.version, ...body },
    }),
  endDelegation: (o: OrgRef, id: string, body: { preview_token?: string; reason?: string } = {}) => kinioFetch(orgUrl(o, '/delegations/' + enc(id)), { method: 'DELETE', body }),
  reviewDelegation: (o: OrgRef, d: Pick<Delegation, 'delegation_id' | 'version'>) =>
    kinioFetch<Delegation>(orgUrl(o, '/delegations/' + enc(d.delegation_id) + '/review'), { method: 'POST', body: { expected_version: d.version } }),

  // 受託方（{org} = D）
  delegatedNodes: (o: OrgRef, dlg: string, q: { parent?: string; q?: string; include?: 'devices'; cursor?: string; limit?: number } = {}) =>
    kinioFetch<{ items: DelegatedNodeItem[]; next_cursor?: string }>(orgUrl(o, `/delegations/${enc(dlg)}/nodes` + qs(q))),
  dAssignments: (o: OrgRef, dlg: string, q: { node_id?: string; granted_by?: string } = {}) =>
    allPages<Assignment>((cursor) => kinioFetch<{ items?: Assignment[]; assignments?: Assignment[]; next_cursor?: string }>(orgUrl(o, `/delegations/${enc(dlg)}/assignments` + qs({ ...q, cursor }))).then((r) => ({ items: r?.items ?? r?.assignments ?? [], next_cursor: r?.next_cursor }))),
  dAssign: (o: OrgRef, dlg: string, nodeId: string, body: AssignmentIn) =>
    kinioFetch<Assignment>(orgUrl(o, `/delegations/${enc(dlg)}/nodes/${enc(nodeId)}/assignments`), { method: 'POST', body }),
  dUpdate: (o: OrgRef, dlg: string, a: Pick<Assignment, 'assignment_id' | 'version'>, body: Partial<AssignmentIn>) =>
    kinioFetch<Assignment>(orgUrl(o, `/delegations/${enc(dlg)}/assignments/${enc(a.assignment_id)}`), { method: 'PATCH', body: { expected_version: a.version, ...body } }),
  dRevoke: (o: OrgRef, dlg: string, a: Pick<Assignment, 'assignment_id' | 'version'>, body: { reason?: string; also_revoke?: string[] } = {}) =>
    kinioFetch(orgUrl(o, `/delegations/${enc(dlg)}/assignments/${enc(a.assignment_id)}`), { method: 'DELETE', body: { expected_version: a.version, ...body } }),
  dApply: (o: OrgRef, dlg: string, body: { assignment_ids?: string[]; expected_version: number }) =>
    kinioFetch<{ applied: number }>(orgUrl(o, `/delegations/${enc(dlg)}/assignments/apply`), { method: 'POST', body }),
};

// ---- 我（即時頁的樹、檢視、群組、訂閱） ----

const me = (p: Pick<Plane, 'api'>, path: string) => `${planeBase(p.api)}/v1/me${path}`;

export const meTreeApi = {
  tree: (p: Pick<Plane, 'api'>, q: { parent?: string; org_id?: string; q?: string; cursor?: string; limit?: number } = {}) => kinioFetch<MeTreePage>(me(p, '/tree' + qs(q))),
  /** 一層全部（翻頁；409 AUTHORIZATION_CHANGED 由呼叫端重來）。 */
  level: (p: Pick<Plane, 'api'>, parent?: string) => allPages<MeTreeItem>((cursor) => meTreeApi.tree(p, { parent, cursor, limit: 1000 })),
  /** 範圍內的設備 key（「我的設備」的 node_id / delegation_id 篩選，同一套去重與歸組）。 */
  deviceKeys: async (p: Pick<Plane, 'api'>, q: { node_id?: string; delegation_id?: string; home_id?: string }) => {
    const out: string[] = [];
    let cursor = '';
    for (let i = 0; i < 50; i++) {
      const r = await kinioFetch<{ devices: { home_id: string; device_ref: string }[]; next_cursor?: string }>(me(p, '/devices' + qs({ ...q, limit: 500, cursor })));
      for (const d of r?.devices ?? []) out.push(`${d.home_id}/${d.device_ref}`);
      cursor = r?.next_cursor ?? '';
      if (!cursor) break;
    }
    return out;
  },

  views: (p: Pick<Plane, 'api'>) => kinioFetch<{ views: View[] }>(me(p, '/views')).then((r) => r?.views ?? []),
  orgViews: (o: OrgRef) => kinioFetch<{ views: View[] }>(orgUrl(o, '/views')).then((r) => r?.views ?? []),
  view: (p: Pick<Plane, 'api'>, owner: { kind: 'user' } | { kind: 'org'; org_id: string }, id: string) =>
    kinioFetch<View>(owner.kind === 'user' ? me(p, '/views/' + enc(id)) : `${planeBase(p.api)}/v1/orgs/${enc(owner.org_id)}/views/${enc(id)}`),
  saveView: (
    p: Pick<Plane, 'api'>,
    owner: { kind: 'user' } | { kind: 'org'; org_id: string },
    v: { view_id?: string; name: string; items: ViewItem[]; expected_version?: number },
  ) => {
    const base = owner.kind === 'user' ? me(p, '/views') : `${planeBase(p.api)}/v1/orgs/${enc(owner.org_id)}/views`;
    const body = { name: v.name, items: v.items, ...(v.expected_version !== undefined ? { expected_version: v.expected_version } : {}) };
    return v.view_id ? kinioFetch<View>(base + '/' + enc(v.view_id), { method: 'PUT', body }) : kinioFetch<View>(base, { method: 'POST', body });
  },
  deleteView: (p: Pick<Plane, 'api'>, owner: { kind: 'user' } | { kind: 'org'; org_id: string }, v: Pick<View, 'view_id' | 'version'>) =>
    kinioFetch((owner.kind === 'user' ? me(p, '/views/') : `${planeBase(p.api)}/v1/orgs/${enc(owner.org_id)}/views/`) + enc(v.view_id) + qs({ expected_version: v.version }), {
      method: 'DELETE',
    }),
  resolveView: (p: Pick<Plane, 'api'>, id: string, cursor?: string) => kinioFetch<Resolved>(`${planeBase(p.api)}/v1/views/${enc(id)}/resolve` + qs({ cursor })),

  groups: (p: Pick<Plane, 'api'>) => kinioFetch<{ groups: DeviceGroup[] }>(me(p, '/device-groups')).then((r) => r?.groups ?? []),
  orgGroups: (o: OrgRef) => kinioFetch<{ groups: DeviceGroup[] }>(orgUrl(o, '/device-groups')).then((r) => r?.groups ?? []),
  saveGroup: (
    p: Pick<Plane, 'api'>,
    owner: { kind: 'user' } | { kind: 'org'; org_id: string },
    g: { group_id?: string; name: string; items: ViewItem[]; expected_version?: number },
  ) => {
    const base = owner.kind === 'user' ? me(p, '/device-groups') : `${planeBase(p.api)}/v1/orgs/${enc(owner.org_id)}/device-groups`;
    const body = { name: g.name, items: g.items, ...(g.expected_version !== undefined ? { expected_version: g.expected_version } : {}) };
    return g.group_id ? kinioFetch<DeviceGroup>(base + '/' + enc(g.group_id), { method: 'PUT', body }) : kinioFetch<DeviceGroup>(base, { method: 'POST', body });
  },
  resolveGroup: (p: Pick<Plane, 'api'>, id: string, cursor?: string) => kinioFetch<Resolved>(`${planeBase(p.api)}/v1/device-groups/${enc(id)}/resolve` + qs({ cursor })),

  subscriptions: (p: Pick<Plane, 'api'>) =>
    Promise.all([
      kinioFetch<{ subscriptions?: ScopeSubscription[]; implicit?: ImplicitNotice[] }>(me(p, '/notification-settings/nodes')),
      kinioFetch<{ subscriptions?: ScopeSubscription[] }>(me(p, '/notification-settings/device-groups')),
    ]).then(([n, g]) => ({ nodes: n?.subscriptions ?? [], groups: g?.subscriptions ?? [], implicit: n?.implicit ?? null })),
  subscribe: (p: Pick<Plane, 'api'>, target: { node_id: string; delegation_id?: string } | { group_id: string }, categories?: ScopeSubscription['categories']) =>
    'node_id' in target
      ? kinioFetch(me(p, '/notification-settings/nodes/' + enc(target.node_id)), { method: 'PUT', body: { ...(target.delegation_id ? { delegation_id: target.delegation_id } : {}), ...(categories ? { categories } : {}) } })
      : kinioFetch(me(p, '/notification-settings/device-groups/' + enc(target.group_id)), { method: 'PUT', body: categories ? { categories } : {} }),
  unsubscribe: (p: Pick<Plane, 'api'>, target: { node_id: string; delegation_id?: string } | { group_id: string }) =>
    'node_id' in target
      ? kinioFetch(me(p, '/notification-settings/nodes/' + enc(target.node_id) + qs({ delegation_id: target.delegation_id })), { method: 'DELETE' })
      : kinioFetch(me(p, '/notification-settings/device-groups/' + enc(target.group_id)), { method: 'DELETE' }),
};

/** 訂閱預設類別：一般偵測、門鈴、呼叫開推播 + 收件匣（SPEC：不帶 categories 的預設）。 */
export const SUB_CATEGORIES = ['device.detection', 'device.doorbell', 'device.call'] as const;
